import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, SlidersHorizontal, X, Briefcase, Loader2, List, Map, ArrowLeft } from "lucide-react";
import JobCard from "../components/JobCard";
import { getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useCountry } from "../context/CountryContext";
import { CountrySearchField } from "../components/CountrySearchField";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "../components/ui/drawer";
import MAP_CONFIG from "../config/map-config";
import { JOB_CATEGORIES, JOB_TYPES, EXPERIENCE_LEVELS, jobTypeLabel, experienceLabel, categoryLabel } from "../config/jobTaxonomy";
import { track } from "../lib/analytics";

const MapView = lazy(() => import("../components/MapView"));
const MapViewMapbox = lazy(() => import("../components/MapViewMapbox"));
const ActiveMapView = process.env.REACT_APP_MAPBOX_TOKEN ? MapViewMapbox : MapView;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOB_TYPES_RAW   = JOB_TYPES;
// job_type is one flat enum in the data model — the mobile drawer presents it as two
// separate selects (Jornada / Tipo de contrato) bound to the same underlying field.
const SHIFT_TYPES_RAW    = ["full-time", "part-time", "hourly", "seasonal"];
const CONTRACT_TYPES_RAW = ["contract", "temporary", "freelance", "internship"];
const EXP_LEVELS_RAW  = EXPERIENCE_LEVELS;
const WORK_MODES_RAW  = ["remote", "hybrid", "onsite"];
const DATE_VALUES     = ["today", "3days", "7days", "30days"];
const SORT_VALUES     = ["newest", "highest_salary", "most_applicants"];
const CATEGORY_VALUES = JOB_CATEGORIES;

const EMPTY_FILTERS = {
  keyword: "", location: "", country_code: "", admin1_name: "", city: "",
  category: "", job_type: "", work_mode: "",
  experience_level: "", salary_min: "", salary_max: "",
  date_posted: "", sort_by: "newest",
  starts_today: false, is_urgent: false,
};

export default function JobsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { t } = useLanguage();
  const { country, setCountry } = useCountry();

  // Exact area the user was viewing on Home's map when they clicked "Buscar en
  // esta zona" — captured once on mount only, so it never overrides the map
  // after the user starts interacting with it here on Jobs.
  const [initialMapBounds, setInitialMapBounds] = useState(() => {
    const b = routerLocation.state?.mapBounds;
    return b ? [[b.swLat, b.swLng], [b.neLat, b.neLng]] : null;
  });

  const WORK_MODE_LABELS = {
    remote: t("jobsPage.workModeRemote"), hybrid: t("jobsPage.workModeHybrid"), onsite: t("jobsPage.workModeOnsite"),
  };
  const DATE_LABELS = {
    today: t("jobsPage.dateToday"), "3days": t("jobsPage.date3days"), "7days": t("jobsPage.date7days"), "30days": t("jobsPage.date30days"),
  };
  const SORT_LABELS = {
    newest: t("jobsPage.sortNewest"), highest_salary: t("jobsPage.sortSalary"), most_applicants: t("jobsPage.sortApplicants"),
  };
  const QUICK_FILTERS = [
    { key: "today",    label: t("jobsPage.quickToday"),    params: { starts_today: true }, accent: "blue" },
    { key: "urgent",   label: t("jobsPage.quickUrgent"),   params: { is_urgent: true },    accent: "pink" },
    { key: "remote",   label: t("jobsPage.quickRemote"),   params: { work_mode: "remote" } },
    { key: "parttime", label: t("jobsPage.quickPartTime"), params: { job_type: "part-time" } },
    { key: "entry",    label: t("jobsPage.quickNoExp"),    params: { experience_level: "entry" } },
  ];
  const RADIUS_OPTIONS = [1, 5, 10, 25, 50];

  // View mode: "list" | "map" | "split"
  const [viewMode, setViewMode] = useState(() =>
    searchParams.get("map") === "true" || searchParams.get("near") === "true" ? "map" : "split"
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [jobs, setJobs]             = useState([]);
  // Separate, unpaginated set that feeds ONLY the map — the list stays paginated
  // (limit=12) but the map must show every mappable job matching the current
  // search, never just the current page (that mismatch was the root cause of
  // "listado 100 vs mapa 12"). MAP_JOBS_LIMIT is a generous safety cap, not a
  // real pagination limit — revisit if the catalog grows well past pilot scale.
  const [mapJobs, setMapJobs]       = useState([]);
  const MAP_JOBS_LIMIT = 400;
  const [total, setTotal]           = useState(0);
  // Tablet/desktop only — when a map cluster of identical/near-identical
  // coordinates is clicked, the LEFT list column swaps to show that
  // cluster's jobs instead of opening a 3rd column. Mobile is untouched
  // (keeps its own bottom drawer inside MapView/MapViewMapbox).
  const [clusterView, setClusterView] = useState(null);
  const [page, setPage]             = useState(() => parseInt(searchParams.get("page"), 10) || 1);
  const [loading, setLoading]       = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedJob, setSelectedJob]   = useState(null);
  // Geolocation state — initialized synchronously from URL so the first fetch already includes lat/lng
  const [userLocation, setUserLocation] = useState(() => {
    const lat = searchParams.get("lat");
    const lng = searchParams.get("lng");
    return lat && lng ? [parseFloat(lat), parseFloat(lng)] : null;
  });
  const [radiusKm, setRadiusKmState] = useState(() => parseFloat(searchParams.get("radius_km")) || parseFloat(sessionStorage.getItem("infocity_radius_km")) || 10);
  const setRadiusKm = (r) => { setRadiusKmState(r); sessionStorage.setItem("infocity_radius_km", String(r)); };
  const [geoLoading, setGeoLoading] = useState(false);
  // null | "denied" | "unavailable" — distinct copy per failure reason (denied vs
  // unavailable/timeout); the legacy `geo_error=1` URL flag (set by Home's own
  // requestGeo redirect) is treated as "unavailable" since it carries no error code.
  const [geoErrorType, setGeoErrorType] = useState(searchParams.get("geo_error") === "1" ? "unavailable" : null);
  // Full País→Región→Provincia→Ciudad selection state for the CountrySearchField —
  // mirrors filters.country_code/admin1_name/city but also keeps the admin1/admin2
  // *codes* (not persisted to the URL) so the select shows the right pre-selection.
  const [locationValue, setLocationValue] = useState(() => ({
    countryCode: searchParams.get("country_code") || country || null,
    admin1Code: null, admin1Name: searchParams.get("admin1_name") || null,
    admin2Code: null, admin2Name: null,
    city: searchParams.get("city") || searchParams.get("location") || "",
  }));
  // Coordinates resolved by CountrySearchField for the currently selected
  // País/Región/Ciudad — drives the map's flyTo, purely visual (never
  // triggers a job search on its own).
  const [mapFlyTo, setMapFlyTo] = useState(null);

  // Builds the filters object from a URLSearchParams instance — shared by the initial
  // state and the back/forward-restore effect so both stay perfectly in sync.
  // `fallbackCountry` is the persisted CountryContext value — applied only when the
  // URL itself carries no explicit country_code, so the selected country survives
  // navigation between Home/Jobs/Map/List without ever overriding an explicit clear.
  const paramsToFilters = (sp, fallbackCountry) => ({
    ...EMPTY_FILTERS,
    keyword:   sp.get("keyword") || "",
    city:      sp.get("city") || sp.get("location") || "",
    country_code: sp.get("country_code") || fallbackCountry || "",
    admin1_name:  sp.get("admin1_name") || "",
    category:  sp.get("category") || "",
    work_mode: sp.get("work_mode") || "",
    job_type:  sp.get("job_type") || "",
    experience_level: sp.get("experience_level") || "",
    salary_min: sp.get("salary_min") || "",
    salary_max: sp.get("salary_max") || "",
    date_posted: sp.get("date_posted") || "",
    sort_by: sp.get("sort_by") || "newest",
    starts_today: sp.get("starts_today") === "true",
    is_urgent:    sp.get("is_urgent") === "true",
  });

  const [filters, setFilters] = useState(() => paramsToFilters(searchParams, country));

  // Shared by both the paginated list request and the unpaginated map request —
  // only `page`/`limit` differ between the two.
  const buildJobParams = (f, loc, radius, p, limit) => {
    const params = new URLSearchParams();
    if (f.keyword)          params.set("keyword", f.keyword);
    if (f.city)             params.set("city", f.city);
    if (f.country_code)     params.set("country_code", f.country_code);
    if (f.admin1_name)      params.set("admin1_name", f.admin1_name);
    if (f.category)         params.set("category", f.category);
    if (f.job_type)         params.set("job_type", f.job_type);
    if (f.work_mode)        params.set("work_mode", f.work_mode);
    if (f.experience_level) params.set("experience_level", f.experience_level);
    if (f.salary_min)       params.set("salary_min", f.salary_min);
    if (f.salary_max)       params.set("salary_max", f.salary_max);
    if (f.date_posted)      params.set("date_posted", f.date_posted);
    if (f.starts_today)     params.set("starts_today", "true");
    if (f.is_urgent)        params.set("is_urgent", "true");
    if (f.sort_by && f.sort_by !== "newest") params.set("sort_by", f.sort_by);
    // "Near me" — real distance filter, backend returns only jobs within radius_km, sorted by distance
    if (loc) {
      params.set("lat", loc[0]);
      params.set("lng", loc[1]);
      params.set("radius_km", radius);
    }
    params.set("page", p); params.set("limit", limit);
    params.set("include_external", "true");
    return params;
  };

  const fetchJobs = useCallback(async (f = filters, p = page, loc = userLocation, radius = radiusKm) => {
    setLoading(true);
    setClusterView(null);
    try {
      const listParams = buildJobParams(f, loc, radius, p, 12);
      const mapParams = buildJobParams(f, loc, radius, 1, MAP_JOBS_LIMIT);
      const [listRes, mapRes] = await Promise.all([
        axios.get(`${API}/jobs?${listParams}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/jobs?${mapParams}`, { headers: getAuthHeaders() }),
      ]);
      setJobs(listRes.data.jobs || []);
      setTotal(listRes.data.total || 0);
      setMapJobs(mapRes.data.jobs || []);
      track("search_performed", {
        keyword: f.keyword || null, city: f.city || null, country_code: f.country_code || null,
        category: f.category || null, results_count: listRes.data.total || 0,
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters, page, userLocation, radiusKm]);

  // Reflects the current search state (filters/page/view/geo) into the URL, omitting
  // any value that's already at its default so shared/reloaded URLs stay clean.
  const syncURL = useCallback((f, p, view, loc, radius) => {
    const sp = new URLSearchParams();
    if (f.keyword)          sp.set("keyword", f.keyword);
    if (f.city)             sp.set("city", f.city);
    if (f.country_code)     sp.set("country_code", f.country_code);
    if (f.admin1_name)      sp.set("admin1_name", f.admin1_name);
    if (f.category)         sp.set("category", f.category);
    if (f.job_type)         sp.set("job_type", f.job_type);
    if (f.work_mode)        sp.set("work_mode", f.work_mode);
    if (f.experience_level) sp.set("experience_level", f.experience_level);
    if (f.salary_min)       sp.set("salary_min", f.salary_min);
    if (f.salary_max)       sp.set("salary_max", f.salary_max);
    if (f.date_posted)      sp.set("date_posted", f.date_posted);
    if (f.sort_by && f.sort_by !== "newest") sp.set("sort_by", f.sort_by);
    if (f.starts_today)     sp.set("starts_today", "true");
    if (f.is_urgent)        sp.set("is_urgent", "true");
    if (p > 1)               sp.set("page", String(p));
    if (view === "map")      sp.set("map", "true");
    if (loc) { sp.set("lat", String(loc[0])); sp.set("lng", String(loc[1])); sp.set("radius_km", String(radius)); }
    setSearchParams(sp, { replace: true });
  }, [setSearchParams]);

  useEffect(() => { fetchJobs(); syncURL(filters, page, viewMode, userLocation, radiusKm); }, [page]);

  // Re-fetch when the user changes the search radius while in "near me" mode
  useEffect(() => {
    if (userLocation) {
      setPage(1); fetchJobs(filters, 1, userLocation, radiusKm);
      syncURL(filters, 1, viewMode, userLocation, radiusKm);
    }
  }, [radiusKm]);

  // Restores filters/page/geo/view whenever the URL changes from OUTSIDE our own
  // syncURL calls — i.e. browser back/forward, a manual reload, or a pasted/shared link.
  useEffect(() => {
    const derivedFilters = paramsToFilters(searchParams, country);
    const derivedPage = parseInt(searchParams.get("page"), 10) || 1;
    const lat = searchParams.get("lat"), lng = searchParams.get("lng");
    const derivedLoc = lat && lng ? [parseFloat(lat), parseFloat(lng)] : null;
    const derivedRadius = parseFloat(searchParams.get("radius_km")) || parseFloat(sessionStorage.getItem("infocity_radius_km")) || 10;
    const derivedView = searchParams.get("map") === "true" ? "map" : viewMode;

    const changed =
      JSON.stringify(derivedFilters) !== JSON.stringify(filters) ||
      derivedPage !== page ||
      JSON.stringify(derivedLoc) !== JSON.stringify(userLocation) ||
      derivedRadius !== radiusKm;

    if (changed) {
      setFilters(derivedFilters);
      setPage(derivedPage);
      setUserLocation(derivedLoc);
      setRadiusKm(derivedRadius);
      if (derivedView !== viewMode) setViewMode(derivedView);
      fetchJobs(derivedFilters, derivedPage, derivedLoc, derivedRadius);
      setLocationValue((prev) => (
        prev.countryCode === (derivedFilters.country_code || null) && prev.admin1Name === (derivedFilters.admin1_name || null) && prev.city === derivedFilters.city
          ? prev
          : { countryCode: derivedFilters.country_code || null, admin1Code: null, admin1Name: derivedFilters.admin1_name || null, admin2Code: null, admin2Name: null, city: derivedFilters.city }
      ));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const clearNearMe = () => {
    setUserLocation(null); setPage(1); fetchJobs(filters, 1, null, radiusKm);
    syncURL(filters, 1, viewMode, null, radiusKm);
  };

  // Only requests the browser's geolocation permission when the candidate
  // explicitly taps "Cerca de mí" — never automatically on page load.
  const requestGeoOnJobsPage = () => {
    if (!navigator.geolocation) { setGeoErrorType("unavailable"); return; }
    setGeoErrorType(null);
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setGeoLoading(false);
        setUserLocation([latitude, longitude]);
        setPage(1);
        fetchJobs(filters, 1, [latitude, longitude], radiusKm);
        syncURL(filters, 1, viewMode, [latitude, longitude], radiusKm);
      },
      (err) => { setGeoLoading(false); setGeoErrorType(err?.code === 1 ? "denied" : "unavailable"); },
      { timeout: 8000 }
    );
  };

  const applyFilters = (f = filters) => {
    setPage(1); fetchJobs(f, 1); setAdvancedOpen(false); setMobileFiltersOpen(false);
    syncURL(f, 1, viewMode, userLocation, radiusKm);
  };
  const setFilter   = (key, val) => setFilters((f) => ({ ...f, [key]: val }));
  // Single source of truth for a job's saved state within this page: the
  // list JobCard, the map popup and the map's mobile bottom-sheet JobCard
  // all point at the same `jobs` array, so a toggle from any surface updates
  // every other surface showing that same job.
  const handleJobSaveToggle = (jobId, isSaved) => {
    setJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, is_saved: isSaved } : j)));
    setMapJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, is_saved: isSaved } : j)));
  };
  const handleLocationChange = (partial) => {
    setLocationValue((prev) => ({ ...prev, ...partial }));
    const isCountryChange = Object.prototype.hasOwnProperty.call(partial, "countryCode");
    const nextFilters = {
      ...filters,
      ...(isCountryChange ? { country_code: partial.countryCode || "" } : {}),
      ...(Object.prototype.hasOwnProperty.call(partial, "admin1Name") ? { admin1_name: partial.admin1Name || "" } : {}),
      ...(Object.prototype.hasOwnProperty.call(partial, "city") ? { city: partial.city || "" } : {}),
    };
    setFilters(nextFilters);
    if (isCountryChange) {
      // Country level auto-applies immediately (list + map + count + page reset),
      // matching Home's behavior — region/provincia/city still require "Buscar"/
      // "Aplicar", same as every other filter in this page (unchanged on purpose).
      // CountrySearchField's own pickCountry() already clears admin1/admin2/city
      // in `partial`, so nextFilters above already drops the dependent levels too.
      // "Cerca de mí" is tied to the PREVIOUS country's location — switching
      // country must drop it (never silently re-apply a stale lat/lng+radius
      // against the new country). Re-enabling near-me for the new country is
      // an explicit, manual action only (no auto geolocation prompt here).
      const clearingNearMe = !!userLocation;
      if (clearingNearMe) setUserLocation(null);
      // Bounds inherited from Home only take priority on the INITIAL load of
      // this page — once the user manually switches country, they're stale
      // and must stop overriding the map; the new country takes control.
      setInitialMapBounds(null);
      setCountry(partial.countryCode || null);
      setPage(1);
      fetchJobs(nextFilters, 1, clearingNearMe ? null : userLocation, radiusKm);
      syncURL(nextFilters, 1, viewMode, clearingNearMe ? null : userLocation, radiusKm);
    }
  };
  const clearAll    = () => {
    const r = { ...EMPTY_FILTERS }; setFilters(r); setPage(1); fetchJobs(r, 1);
    syncURL(r, 1, viewMode, userLocation, radiusKm);
    setCountry(null);
    setInitialMapBounds(null);
    setLocationValue({ countryCode: null, admin1Code: null, admin1Name: null, admin2Code: null, admin2Name: null, city: "" });
    setMapFlyTo(null);
  };

  const handleQuickFilter = (params) => {
    const isActive = Object.entries(params).every(([k, v]) => filters[k] === v);
    const newFilters = { ...filters };
    Object.keys(params).forEach((k) => { newFilters[k] = isActive ? EMPTY_FILTERS[k] : params[k]; });
    setFilters(newFilters);
    setPage(1);
    fetchJobs(newFilters, 1);
    syncURL(newFilters, 1, viewMode, userLocation, radiusKm);
  };

  const changeView = (mode) => {
    setViewMode(mode);
    syncURL(filters, page, mode, userLocation, radiusKm);
  };

  const handleSearchAreaOnMap = (bounds) => {
    if (!bounds) return;
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const countryParam = filters.country_code ? `&country_code=${filters.country_code}` : "";
    // Load jobs in bounds — feeds BOTH the list and the map with the exact same
    // bounds-limited set the user asked for ("Buscar en esta zona").
    axios.get(`${API}/jobs/in-bounds?swlat=${sw.lat.toFixed(4)}&swlng=${sw.lng.toFixed(4)}&nelat=${ne.lat.toFixed(4)}&nelng=${ne.lng.toFixed(4)}&limit=${MAP_JOBS_LIMIT}&include_external=true${countryParam}`)
      .then(res => { setJobs(res.data.jobs || []); setMapJobs(res.data.jobs || []); setTotal(res.data.count ?? (res.data.jobs || []).length); })
      .catch(() => {});
  };

  const totalPages = Math.ceil(total / 12);
  const activeFilterCount = [
    filters.category, filters.job_type, filters.work_mode,
    filters.experience_level, (filters.salary_min || filters.salary_max) ? "salary" : "", filters.date_posted,
    filters.starts_today ? "starts_today" : "", filters.is_urgent ? "is_urgent" : "",
    filters.sort_by !== "newest" ? filters.sort_by : "",
    filters.country_code, filters.admin1_name, filters.city,
  ].filter(Boolean).length;

  // Mobile: determine default view (list or map)
  const mobileView = viewMode === "map" ? "map" : "list";

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col overflow-x-hidden max-w-[100vw]" data-testid="jobs-page">

      {/* ── Sticky search bar ── */}
      <div className="bg-white border-b border-[#E5E7EB] py-3 px-4 z-30">
        <div className="container mx-auto max-w-5xl">
          <form onSubmit={(e) => { e.preventDefault(); applyFilters(); }}
            className="flex gap-2 items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8] pointer-events-none" />
              <input data-testid="jobs-keyword-input" type="text" value={filters.keyword}
                onChange={(e) => setFilter("keyword", e.target.value)}
                placeholder={t("filters.keyword")}
                className="w-full h-10 pl-9 pr-3 text-base lg:text-sm font-poppins text-[#1A1A1A] placeholder-[#94A3B8] bg-[#F8FAFC] rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#028CF8] focus:bg-white transition-all" />
            </div>
            <div className="relative hidden sm:block w-52 flex-shrink-0">
              <CountrySearchField
                value={locationValue}
                onChange={handleLocationChange}
                onLocate={setMapFlyTo}
                testidPrefix="jobs-location"
                triggerHeight="h-10"
                triggerBg="bg-[#F8FAFC]"
              />
            </div>
            <button type="submit" data-testid="jobs-search-button"
              className="h-10 w-10 bg-[#028CF8] hover:bg-[#0270C5] text-white rounded-xl flex items-center justify-center flex-shrink-0 transition-all">
              <Search className="h-4 w-4" />
            </button>
            {/* Desktop: filter toggle */}
            <button type="button" data-testid="jobs-filter-toggle"
              onClick={() => setAdvancedOpen(!advancedOpen)}
              className={`hidden lg:flex h-10 px-4 items-center gap-2 rounded-xl border text-sm font-semibold font-poppins transition-all flex-shrink-0 ${advancedOpen || activeFilterCount > 0 ? "border-[#028CF8] text-[#028CF8] bg-[#028CF8]/5" : "border-[#E5E7EB] text-[#64748B] bg-[#F8FAFC]"}`}>
              <SlidersHorizontal className="h-4 w-4" />
              {t("filters.title")}
              {activeFilterCount > 0 && <span className="h-5 w-5 rounded-full bg-[#028CF8] text-white text-xs flex items-center justify-center">{activeFilterCount}</span>}
            </button>
            {/* Mobile/tablet: opens the bottom-sheet filters drawer */}
            <button type="button" data-testid="jobs-filter-toggle-mobile" aria-label={t("filters.title")}
              onClick={() => setMobileFiltersOpen(true)}
              className={`lg:hidden relative h-10 px-3 flex items-center gap-1.5 rounded-xl border text-sm font-semibold font-poppins transition-all flex-shrink-0 ${activeFilterCount > 0 ? "border-[#028CF8] text-[#028CF8] bg-[#028CF8]/5" : "border-[#E5E7EB] text-[#64748B] bg-[#F8FAFC]"}`}>
              <SlidersHorizontal className="h-4 w-4" />
              <span className="hidden sm:inline">{t("filters.title")}</span>
              {activeFilterCount > 0 && (
                <span data-testid="mobile-filters-badge" className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#028CF8] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* ── Geolocation error banner (permission denied / timeout) ── */}
      {geoErrorType && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2" data-testid="geo-error-banner">
          <div className="container mx-auto max-w-5xl flex items-center justify-between gap-3">
            <span className="text-xs text-amber-800 font-poppins">
              {geoErrorType === "denied" ? t("jobsPage.geoErrorDenied") : t("jobsPage.geoErrorBanner")}
            </span>
            <button onClick={() => setGeoErrorType(null)} className="text-xs text-amber-700 font-semibold font-poppins hover:underline flex-shrink-0">
              {t("jobsPage.dismiss")}
            </button>
          </div>
        </div>
      )}

      {/* ── Desktop/tablet: quick filter chips + count (scrollable) ── */}
      <div className="hidden lg:block bg-white border-b border-[#E5E7EB] py-2 px-4">
        <div className="container mx-auto max-w-5xl flex items-center gap-2">
          <div className="flex items-center gap-3 overflow-x-auto flex-1 min-w-0" style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
          <span className="text-sm text-[#64748B] font-poppins flex-shrink-0" data-testid="jobs-count">
            {loading ? t("filters.searching") : <><strong className="text-[#1A1A1A]">{total}</strong> {t("jobsPage.jobsSuffix")}</>}
          </span>
          <div className="w-px bg-[#E5E7EB] h-5 flex-shrink-0" />
          {userLocation ? (
            <>
              <span data-testid="near-me-badge" style={{
                display: "flex", alignItems: "center", gap: "6px", padding: "4px 10px", borderRadius: "999px",
                fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif", flexShrink: 0,
                backgroundColor: "#028CF8", color: "white",
              }}>
                <MapPin style={{ width: 11, height: 11 }} /> {t("jobsPage.nearMeBadge")}
              </span>
              <span className="text-xs text-[#64748B] font-poppins flex-shrink-0">{t("jobsPage.radiusLabel")}:</span>
              {RADIUS_OPTIONS.map((r) => (
                <button key={r} data-testid={`radius-${r}`} onClick={() => setRadiusKm(r)}
                  style={{
                    padding: "4px 10px", borderRadius: "999px", border: "1px solid",
                    fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif",
                    flexShrink: 0, cursor: "pointer", transition: "all 0.15s",
                    borderColor: radiusKm === r ? "#028CF8" : "#E5E7EB",
                    backgroundColor: radiusKm === r ? "#028CF8" : "white",
                    color: radiusKm === r ? "white" : "#64748B",
                  }}>
                  {r}km
                </button>
              ))}
              <button onClick={clearNearMe} data-testid="clear-near-me" style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#94A3B8", background: "none", border: "none", cursor: "pointer", fontFamily: "Poppins, sans-serif", flexShrink: 0 }}>
                <X style={{ width: 12, height: 12 }} /> {t("jobsPage.exitNearMe")}
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="quick-filter-near-me"
                onClick={requestGeoOnJobsPage}
                disabled={geoLoading}
                style={{
                  display: "flex", alignItems: "center", gap: "5px",
                  padding: "4px 12px", borderRadius: "999px", border: "1px solid #E5E7EB",
                  fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif",
                  whiteSpace: "nowrap", flexShrink: 0, cursor: geoLoading ? "default" : "pointer",
                  backgroundColor: "white", color: "#64748B",
                }}>
                {geoLoading ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" /> : <MapPin style={{ width: 12, height: 12 }} />}
                {geoLoading ? t("jobsPage.geoRequesting") : t("jobsPage.quickNearMe")}
              </button>
              {QUICK_FILTERS.map(({ key, label, params, accent }) => {
              const isActive = Object.entries(params).every(([k, v]) => filters[k] === v);
              const activeColor = accent === "pink" ? "#F55FA5" : "#028CF8";
              return (
                <button key={key}
                  data-testid={`quick-filter-${key}`}
                  onClick={() => handleQuickFilter(params)}
                  style={{
                    padding: "4px 12px", borderRadius: "999px", border: "1px solid",
                    fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif",
                    whiteSpace: "nowrap", flexShrink: 0, cursor: "pointer", transition: "all 0.15s",
                    borderColor: isActive ? activeColor : "#E5E7EB",
                    backgroundColor: isActive ? activeColor : "white",
                    color: isActive ? "white" : "#64748B",
                  }}>
                  {label}
                </button>
              );
            })}
            </>
          )}
          {activeFilterCount > 0 && (
            <button onClick={clearAll} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#94A3B8", background: "none", border: "none", cursor: "pointer", fontFamily: "Poppins, sans-serif", flexShrink: 0 }}>
              <X style={{ width: 12, height: 12 }} /> {t("jobsPage.clear")}
            </button>
          )}
          </div>
        </div>
      </div>

      {/* ── Mobile: single-row essentials (count + near-me badge) + List/Map toggle, zero horizontal scroll — the rest lives in the Filtros drawer ── */}
      <div className="lg:hidden bg-white border-b border-[#E5E7EB] py-2 px-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#64748B] font-poppins flex-1 min-w-0 truncate" data-testid="jobs-count-mobile">
            {loading ? t("filters.searching") : <><strong className="text-[#1A1A1A]">{total}</strong> {t("jobsPage.jobsSuffix")}</>}
          </span>
          {userLocation && (
            <button onClick={clearNearMe} data-testid="near-me-badge-mobile" style={{
              display: "flex", alignItems: "center", gap: "4px", padding: "4px 9px", borderRadius: "999px",
              fontSize: "11px", fontWeight: 600, fontFamily: "Poppins, sans-serif", flexShrink: 0,
              backgroundColor: "#028CF8", color: "white", border: "none", cursor: "pointer",
            }}>
              <MapPin style={{ width: 10, height: 10 }} /> {radiusKm}km <X style={{ width: 10, height: 10 }} />
            </button>
          )}
          {isMobile && (
            <div className="flex rounded-xl border border-[#E5E7EB] overflow-hidden flex-shrink-0 bg-[#F8FAFC]">
              <button type="button" onClick={() => changeView("list")} data-testid="view-list-btn"
                className={`h-9 px-3 flex items-center gap-1 text-xs font-semibold font-poppins transition-all ${mobileView === "list" ? "bg-[#028CF8] text-white" : "text-[#64748B]"}`}>
                <List className="h-3.5 w-3.5" /> {t("jobsPage.listView")}
              </button>
              <button type="button" onClick={() => changeView("map")} data-testid="view-map-btn"
                className={`h-9 px-3 flex items-center gap-1 text-xs font-semibold font-poppins transition-all ${mobileView === "map" ? "bg-[#028CF8] text-white" : "text-[#64748B]"}`}>
                <Map className="h-3.5 w-3.5" /> {t("jobsPage.mapView")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Advanced filters panel ── */}
      {advancedOpen && (
        <div className="bg-white border-b border-[#E5E7EB] px-4 py-4" data-testid="filters-panel">
          <div className="container mx-auto max-w-5xl">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
              {[
                { label: t("jobsPage.sectorLabel"),     key: "category",         options: CATEGORY_VALUES.map(v => ({ label: categoryLabel(v, t), value: v })) },
                { label: t("jobsPage.jobTypeLabel"),     key: "job_type",         options: JOB_TYPES_RAW.map(v => ({ label: jobTypeLabel(v, t), value: v })) },
                { label: t("jobsPage.workModeLabel"),    key: "work_mode",        options: WORK_MODES_RAW.map(v => ({ label: WORK_MODE_LABELS[v], value: v })) },
                { label: t("jobsPage.experienceLabel"),  key: "experience_level", options: EXP_LEVELS_RAW.map(v => ({ label: experienceLabel(v, t), value: v })) },
              ].map(({ label, key, options }) => (
                <div key={key}>
                  <label style={{ display: "block", fontSize: "10px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px", fontFamily: "Poppins, sans-serif" }}>{label}</label>
                  <select value={filters[key]} onChange={(e) => setFilter(key, e.target.value)}
                    style={{ width: "100%", height: "38px", border: "1px solid #E5E7EB", borderRadius: "9px", padding: "0 8px", fontSize: "13px", fontFamily: "Poppins, sans-serif", background: "white", outline: "none", color: filters[key] ? "#0F172A" : "#94A3B8" }}>
                    <option value="">{t("jobsPage.anyOption")}</option>
                    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              ))}
              {/* Date posted */}
              <div>
                <label style={{ display: "block", fontSize: "10px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px", fontFamily: "Poppins, sans-serif" }}>{t("jobsPage.datePostedLabel")}</label>
                <select value={filters.date_posted} onChange={(e) => setFilter("date_posted", e.target.value)}
                  style={{ width: "100%", height: "38px", border: "1px solid #E5E7EB", borderRadius: "9px", padding: "0 8px", fontSize: "13px", fontFamily: "Poppins, sans-serif", background: "white", outline: "none" }}>
                  <option value="">{t("jobsPage.anyTime")}</option>
                  {DATE_VALUES.map(v => <option key={v} value={v}>{DATE_LABELS[v]}</option>)}
                </select>
              </div>
              {/* Sort */}
              <div>
                <label style={{ display: "block", fontSize: "10px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px", fontFamily: "Poppins, sans-serif" }}>{t("jobsPage.sortLabel")}</label>
                <select value={filters.sort_by} onChange={(e) => setFilter("sort_by", e.target.value)}
                  style={{ width: "100%", height: "38px", border: "1px solid #E5E7EB", borderRadius: "9px", padding: "0 8px", fontSize: "13px", fontFamily: "Poppins, sans-serif", background: "white", outline: "none" }}>
                  {SORT_VALUES.map(v => <option key={v} value={v}>{SORT_LABELS[v]}</option>)}
                </select>
              </div>
            </div>
            {/* Salary range */}
            <div className="flex items-center gap-3 mb-4">
              <label style={{ fontSize: "10px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "Poppins, sans-serif", whiteSpace: "nowrap" }}>{t("jobsPage.salaryLabel")}</label>
              <input type="number" value={filters.salary_min} onChange={(e) => setFilter("salary_min", e.target.value)} placeholder={t("jobsPage.salaryMinPlaceholder")}
                style={{ width: "90px", height: "38px", border: "1px solid #E5E7EB", borderRadius: "9px", padding: "0 8px", fontSize: "13px", fontFamily: "Poppins, sans-serif", outline: "none" }} />
              <span style={{ color: "#94A3B8", fontSize: "13px" }}>—</span>
              <input type="number" value={filters.salary_max} onChange={(e) => setFilter("salary_max", e.target.value)} placeholder={t("jobsPage.salaryMaxPlaceholder")}
                style={{ width: "90px", height: "38px", border: "1px solid #E5E7EB", borderRadius: "9px", padding: "0 8px", fontSize: "13px", fontFamily: "Poppins, sans-serif", outline: "none" }} />
            </div>
            <div className="flex gap-2 pt-3 border-t border-[#E5E7EB]">
              <button data-testid="apply-filters-button" onClick={() => applyFilters()}
                style={{ height: "38px", padding: "0 18px", background: "#028CF8", color: "white", borderRadius: "9px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", border: "none", cursor: "pointer" }}>
                {t("filters.applyFilters")}
              </button>
              <button onClick={clearAll}
                style={{ height: "38px", padding: "0 14px", background: "white", color: "#64748B", borderRadius: "9px", fontSize: "13px", fontFamily: "Poppins, sans-serif", border: "1px solid #E5E7EB", cursor: "pointer" }}>
                {t("jobsPage.clear")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT — split view desktop / toggle mobile ── */}
      <div className="flex-1 flex" style={{ height: isMobile ? "auto" : "calc(100vh - 148px)", overflow: isMobile ? "visible" : "hidden" }}>

        {/* LEFT: Results list */}
        {(!isMobile || mobileView === "list") && (
          <div
            className={`${isMobile ? "w-full" : "w-[40%]"} overflow-y-auto bg-[#F8FAFC]`}
            style={{ height: isMobile ? "auto" : "calc(100vh - 148px)" }}
          >
            <div className="p-4">
              {!isMobile && clusterView ? (
                <>
                  <button
                    type="button"
                    data-testid="cluster-view-back-button"
                    onClick={() => setClusterView(null)}
                    className="flex items-center gap-1.5 text-sm font-semibold font-poppins text-[#028CF8] hover:underline mb-3"
                  >
                    <ArrowLeft style={{ width: 16, height: 16 }} />
                    {t("jobsPage.backToAllJobs")}
                  </button>
                  <p className="text-sm font-poppins text-[#64748B] mb-3" data-testid="cluster-view-count">
                    {clusterView.length} {t("map.jobsAtLocation")}
                  </p>
                  <div className="space-y-3" data-testid="cluster-jobs-grid">
                    {clusterView.map((job) => (
                      <div key={job.job_id} data-testid={`cluster-job-${job.job_id}`}>
                        <JobCard job={job} onSaveToggle={handleJobSaveToggle} />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
              <>
              {loading ? (
                <div className="space-y-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-[#E5E7EB] animate-pulse" style={{ height: "160px" }} />
                  ))}
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-16" data-testid="no-jobs">
                  <Briefcase style={{ width: 48, height: 48, color: "#CBD5E1", margin: "0 auto 12px" }} />
                  {userLocation ? (
                    <>
                      <p className="font-poppins font-semibold text-[#64748B] mb-2">{t("jobsPage.noJobsInRadiusTitle")}</p>
                      <p className="text-sm text-[#94A3B8] mb-4">{t("jobsPage.noJobsInRadiusDesc").replace("{radius}", radiusKm)}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-poppins font-semibold text-[#64748B] mb-2">{t("filters.noJobs")}</p>
                      <p className="text-sm text-[#94A3B8] mb-4">{t("filters.noJobsDesc")}</p>
                    </>
                  )}
                  <button onClick={userLocation ? clearNearMe : clearAll} className="text-sm text-[#028CF8] font-semibold font-poppins hover:underline">
                    {userLocation ? t("jobsPage.exitNearMe") : t("filters.clearFilters")}
                  </button>
                </div>
              ) : (
                <div className="space-y-3" data-testid="jobs-grid">
                  {jobs.map((job) => (
                    <div key={job.job_id}
                      style={{
                        borderRadius: "16px",
                        transition: "all 0.15s ease",
                        ...(selectedJob?.job_id === job.job_id
                          ? { boxShadow: "0 0 0 2px #028CF8", background: "rgba(2,140,248,0.05)" }
                          : {}),
                      }}
                      data-testid={`job-list-item-${job.job_id}`}
                      onMouseEnter={!isMobile ? () => setSelectedJob(job) : undefined}>
                      <JobCard job={job} compact={!isMobile} onSaveToggle={handleJobSaveToggle} />
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && !loading && (
                <div className="flex justify-center gap-2 mt-6 pb-4">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                    className="h-9 px-4 rounded-xl border border-[#E5E7EB] text-sm font-poppins text-[#64748B] hover:bg-white disabled:opacity-40 transition-all">
                    {t("jobsPage.prevPage")}
                  </button>
                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    const p = i + 1;
                    return (
                      <button key={p} onClick={() => setPage(p)}
                        className={`h-9 w-9 rounded-xl text-sm font-semibold font-poppins transition-all ${page === p ? "bg-[#028CF8] text-white shadow-md" : "border border-[#E5E7EB] text-[#64748B] hover:bg-white"}`}>
                        {p}
                      </button>
                    );
                  })}
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="h-9 px-4 rounded-xl border border-[#E5E7EB] text-sm font-poppins text-[#64748B] hover:bg-white disabled:opacity-40 transition-all">
                    {t("jobsPage.nextPage")}
                  </button>
                </div>
              )}
              </>
              )}
            </div>
          </div>
        )}

        {/* RIGHT: Map panel — explicit height for Leaflet */}
        {(!isMobile || mobileView === "map") && (
          <div
            className={`${isMobile ? "w-full" : "w-[60%]"} border-l border-[#E5E7EB]`}
            style={{ height: isMobile ? "calc(100vh - 148px)" : "calc(100vh - 148px)", position: "relative", flexShrink: 0 }}
          >
            <Suspense fallback={
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#F8FAFC" }}>
                <Loader2 style={{ width: 32, height: 32, color: "#028CF8", animation: "spin 1s linear infinite" }} />
              </div>
            }>
              <ActiveMapView
                jobs={mapJobs}
                height="calc(100vh - 148px)"
                initialBounds={initialMapBounds}
                fallbackCenter={filters.country_code ? MAP_CONFIG.countryDefaults[filters.country_code]?.center : undefined}
                fallbackZoom={filters.country_code ? MAP_CONFIG.countryDefaults[filters.country_code]?.zoom : undefined}
                userLocation={userLocation}
                flyToTarget={mapFlyTo}
                selectedJobId={selectedJob?.job_id}
                onJobSelect={(job) => setSelectedJob(job)}
                onJobSaveToggle={handleJobSaveToggle}
                showSearchArea
                onSearchArea={handleSearchAreaOnMap}
                onClusterSelect={(jobsInCluster) => setClusterView(jobsInCluster)}
              />
            </Suspense>
          </div>
        )}
      </div>

      {/* ── Mobile/tablet filters bottom-sheet ── */}
      <Drawer open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <DrawerContent data-testid="mobile-filters-drawer">
          <DrawerHeader>
            <DrawerTitle className="font-poppins">{t("filters.title")}</DrawerTitle>
            <DrawerDescription className="sr-only">{t("filters.title")}</DrawerDescription>
          </DrawerHeader>
          <div className="px-4 overflow-y-auto" style={{ maxHeight: "60vh" }}>
            <div className="space-y-4 pb-4">
              {/* Quick filter toggles + near-me radius — moved here from the top bar so mobile never scrolls horizontally */}
              <div>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{t("jobsPage.quickFiltersLabel")}</label>
                <div className="flex flex-wrap gap-2">
                  {!userLocation && (
                    <button type="button" data-testid="mobile-quick-filter-near-me"
                      onClick={requestGeoOnJobsPage} disabled={geoLoading}
                      style={{
                        display: "flex", alignItems: "center", gap: "6px",
                        padding: "6px 14px", borderRadius: "999px", border: "1px solid #E5E7EB",
                        fontSize: "14.5px", fontWeight: 600, fontFamily: "Poppins, sans-serif",
                        cursor: geoLoading ? "default" : "pointer", backgroundColor: "white", color: "#64748B",
                      }}>
                      {geoLoading ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <MapPin style={{ width: 13, height: 13 }} />}
                      {geoLoading ? t("jobsPage.geoRequesting") : t("jobsPage.quickNearMe")}
                    </button>
                  )}
                  {QUICK_FILTERS.map(({ key, label, params, accent }) => {
                    const isActive = Object.entries(params).every(([k, v]) => filters[k] === v);
                    const activeColor = accent === "pink" ? "#F55FA5" : "#028CF8";
                    return (
                      <button key={key} type="button" data-testid={`mobile-quick-filter-${key}`}
                        onClick={() => handleQuickFilter(params)}
                        style={{
                          padding: "6px 14px", borderRadius: "999px", border: "1px solid",
                          fontSize: "14.5px", fontWeight: 600, fontFamily: "Poppins, sans-serif",
                          cursor: "pointer", transition: "all 0.15s",
                          borderColor: isActive ? activeColor : "#E5E7EB",
                          backgroundColor: isActive ? activeColor : "white",
                          color: isActive ? "white" : "#64748B",
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {userLocation && (
                <div>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{t("jobsPage.radiusLabel")}</label>
                  <div className="flex flex-wrap gap-2 items-center">
                    {RADIUS_OPTIONS.map((r) => (
                      <button key={r} type="button" data-testid={`mobile-radius-${r}`} onClick={() => setRadiusKm(r)}
                        style={{
                          padding: "6px 14px", borderRadius: "999px", border: "1px solid",
                          fontSize: "14.5px", fontWeight: 600, fontFamily: "Poppins, sans-serif",
                          cursor: "pointer", transition: "all 0.15s",
                          borderColor: radiusKm === r ? "#028CF8" : "#E5E7EB",
                          backgroundColor: radiusKm === r ? "#028CF8" : "white",
                          color: radiusKm === r ? "white" : "#64748B",
                        }}>
                        {r}km
                      </button>
                    ))}
                    <button type="button" onClick={clearNearMe} data-testid="mobile-clear-near-me" style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#94A3B8", background: "none", border: "none", cursor: "pointer", fontFamily: "Poppins, sans-serif" }}>
                      <X style={{ width: 12, height: 12 }} /> {t("jobsPage.exitNearMe")}
                    </button>
                  </div>
                </div>
              )}
              <div>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{t("filters.keyword")}</label>
                <input data-testid="mobile-filter-keyword" type="text" value={filters.keyword} onChange={(e) => setFilter("keyword", e.target.value)}
                  placeholder={t("filters.keyword")}
                  style={{ width: "100%", height: "46px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 14px", fontSize: "16px", fontFamily: "Poppins, sans-serif", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{t("jobsPage.locationLabel")}</label>
                <div className="rounded-xl border border-[#E5E7EB] p-3 bg-[#F8FAFC]">
                  <CountrySearchField
                    value={locationValue}
                    onChange={handleLocationChange}
                    onLocate={setMapFlyTo}
                    inline
                    testidPrefix="mobile-filter-location"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { label: t("jobsPage.workModeLabel"),    key: "work_mode",        testid: "mobile-filter-workmode",  options: WORK_MODES_RAW.map(v => ({ label: WORK_MODE_LABELS[v], value: v })) },
                  { label: t("jobsPage.datePostedLabel"),  key: "date_posted",      testid: "mobile-filter-date",      options: DATE_VALUES.map(v => ({ label: DATE_LABELS[v], value: v })), anyLabel: t("jobsPage.anyTime") },
                  { label: t("jobsPage.sectorLabel"),      key: "category",         testid: "mobile-filter-category",  options: CATEGORY_VALUES.map(v => ({ label: categoryLabel(v, t), value: v })) },
                  { label: t("jobsPage.experienceLabel"),  key: "experience_level", testid: "mobile-filter-experience", options: EXP_LEVELS_RAW.map(v => ({ label: experienceLabel(v, t), value: v })) },
                  { label: t("jobsPage.jobTypeLabel"),      key: "job_type",        testid: "mobile-filter-shift",     options: SHIFT_TYPES_RAW.map(v => ({ label: jobTypeLabel(v, t), value: v })) },
                  { label: t("jobsPage.contractTypeLabel"), key: "job_type",        testid: "mobile-filter-contract",  options: CONTRACT_TYPES_RAW.map(v => ({ label: jobTypeLabel(v, t), value: v })) },
                ].map(({ label, key, testid, options, anyLabel }) => (
                  <div key={testid}>
                    <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{label}</label>
                    <select data-testid={testid}
                      value={options.some(o => o.value === filters[key]) ? filters[key] : ""}
                      onChange={(e) => setFilter(key, e.target.value)}
                      style={{ width: "100%", height: "46px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 10px", fontSize: "16px", fontFamily: "Poppins, sans-serif", background: "white", outline: "none", color: filters[key] ? "#0F172A" : "#334155" }}>
                      <option value="">{anyLabel || t("jobsPage.anyOption")}</option>
                      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              <div>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", fontFamily: "Poppins, sans-serif" }}>{t("jobsPage.salaryLabel")}</label>
                <div className="flex items-center gap-3">
                  <input data-testid="mobile-filter-salary-min" type="number" value={filters.salary_min} onChange={(e) => setFilter("salary_min", e.target.value)} placeholder={t("jobsPage.salaryMinPlaceholder")}
                    style={{ flex: 1, minWidth: 0, height: "46px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 14px", fontSize: "16px", fontFamily: "Poppins, sans-serif", outline: "none", boxSizing: "border-box" }} />
                  <span style={{ color: "#94A3B8", fontSize: "13px", flexShrink: 0 }}>—</span>
                  <input data-testid="mobile-filter-salary-max" type="number" value={filters.salary_max} onChange={(e) => setFilter("salary_max", e.target.value)} placeholder={t("jobsPage.salaryMaxPlaceholder")}
                    style={{ flex: 1, minWidth: 0, height: "46px", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "0 14px", fontSize: "16px", fontFamily: "Poppins, sans-serif", outline: "none", boxSizing: "border-box" }} />
                </div>
              </div>
            </div>
          </div>
          {/* Fixed actions */}
          <div className="flex gap-2 px-4 border-t border-[#E5E7EB] pt-3" style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}>
            <button data-testid="mobile-filter-clear-button" onClick={clearAll}
              style={{ flex: 1, height: "46px", background: "white", color: "#64748B", borderRadius: "10px", fontSize: "16px", fontWeight: 600, fontFamily: "Poppins, sans-serif", border: "1px solid #E5E7EB", cursor: "pointer" }}>
              {t("jobsPage.clear")}
            </button>
            <button data-testid="mobile-filter-apply-button" onClick={() => applyFilters()}
              style={{ flex: 1, height: "46px", background: "#028CF8", color: "white", borderRadius: "10px", fontSize: "16px", fontWeight: 700, fontFamily: "Poppins, sans-serif", border: "none", cursor: "pointer" }}>
              {t("filters.applyFilters")}
            </button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
